'use client';

import { useState, useEffect } from 'react';
import {
  LuMenu,
  LuX,
  LuChevronDown,
  LuLogIn,
  LuMapPin,
  LuLandmark,
  LuHouse,
  LuMonitor,
  LuFlame,
  LuBike,
  LuPackage,
  LuLayoutDashboard,
  LuUser,
  LuRuler,
  LuCompass,
  LuTarget,
  LuDatabase,
  LuGlobe,
  LuRadar,
  LuKey,
  LuClipboardList,
  LuBuilding,
  LuPrinter,
  LuLaptop,
  LuGraduationCap,
  LuWifi,
  LuShield,
  LuArrowRight,
} from 'react-icons/lu';
import Link from 'next/link';
import { useAuth, UserButton, useUser } from '@clerk/nextjs';
import LoginModal from '@/components/auth/LoginModal';

const NavigationBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const { isSignedIn } = useAuth();
  const { user } = useUser();

  // Get user's full name
  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
    : 'User';

  const mainMenuItems = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    {
      name: 'Services',
      href: '#',
      submenu: [
        {
          name: 'Land Survey',
          description:
            'Professional land surveying and boundary marking services',
          href: '/public/services/land-survey',
          icon: <LuMapPin className='h-6 w-6 text-[#00B0F0]' />,
          color: 'from-[#00B0F0]/20 to-[#0090D0]/20',
          hoverColor:
            'group-hover:from-[#00B0F0]/30 group-hover:to-[#0090D0]/30',
          accentColor: '#00B0F0',
          bgColor: 'bg-[#00B0F0]/10',
          borderColor: 'border-[#00B0F0]/30',
          subServices: [
            {
              name: 'Land Subdivision',
              icon: <LuRuler className='h-5 w-5' />,
              description:
                'Professional land subdivision with complete documentation',
            },
            {
              name: 'Beacon Installation',
              icon: <LuTarget className='h-5 w-5' />,
              description: 'Accurate beacon installation and boundary marking',
            },
            {
              name: 'Area Confirmation',
              icon: <LuCompass className='h-5 w-5' />,
              description: 'Precise area measurement and confirmation services',
            },
          ],
        },
        {
          name: 'GIS & Mapping',
          description:
            'Geographic Information Systems and digital mapping solutions',
          href: '/public/services/gis-mapping',
          icon: <LuLandmark className='h-6 w-6 text-[#00B050]' />,
          color: 'from-[#00B050]/20 to-[#009040]/20',
          hoverColor:
            'group-hover:from-[#00B050]/30 group-hover:to-[#009040]/30',
          accentColor: '#00B050',
          bgColor: 'bg-[#00B050]/10',
          borderColor: 'border-[#00B050]/30',
          subServices: [
            {
              name: 'GIS Data Collection',
              icon: <LuDatabase className='h-5 w-5' />,
              description: 'Field data collection and spatial analysis',
            },
            {
              name: 'Spatial Analysis',
              icon: <LuGlobe className='h-5 w-5' />,
              description: 'Advanced geospatial analysis and visualization',
            },
            {
              name: 'Topographic Survey',
              icon: <LuRadar className='h-5 w-5' />,
              description: 'Detailed topographic mapping and terrain analysis',
            },
          ],
        },
        {
          name: 'Property Management',
          description: 'Complete property management and maintenance services',
          href: '/public/services/property-management',
          icon: <LuHouse className='h-6 w-6 text-[#9747FF]' />,
          color: 'from-[#9747FF]/20 to-[#7030A0]/20',
          hoverColor:
            'group-hover:from-[#9747FF]/30 group-hover:to-[#7030A0]/30',
          accentColor: '#9747FF',
          bgColor: 'bg-[#9747FF]/10',
          borderColor: 'border-[#9747FF]/30',
          subServices: [
            {
              name: 'Property Maintenance',
              icon: <LuKey className='h-5 w-5' />,
              description: 'Regular inspections and preventive maintenance',
            },
            {
              name: 'Rental Management',
              icon: <LuClipboardList className='h-5 w-5' />,
              description: 'Tenant screening and rent collection services',
            },
            {
              name: 'Real Estate Services',
              icon: <LuBuilding className='h-5 w-5' />,
              description: 'Property buying, selling and valuation services',
            },
          ],
        },
        {
          name: 'Cyber Services',
          description:
            'IT solutions, web development, and cybersecurity services',
          href: '/public/services/cyber-services',
          icon: <LuMonitor className='h-6 w-6 text-[#4B56D2]' />,
          color: 'from-[#4B56D2]/20 to-[#3A44B1]/20',
          hoverColor:
            'group-hover:from-[#4B56D2]/30 group-hover:to-[#3A44B1]/30',
          accentColor: '#4B56D2',
          bgColor: 'bg-[#4B56D2]/10',
          borderColor: 'border-[#4B56D2]/30',
          subServices: [
            {
              name: 'Printing Services',
              icon: <LuPrinter className='h-5 w-5' />,
              description: 'High-quality printing for documents and designs',
            },
            {
              name: 'Computer Training',
              icon: <LuGraduationCap className='h-5 w-5' />,
              description:
                'Personalized computer training for all skill levels',
            },
            {
              name: 'Internet Services',
              icon: <LuWifi className='h-5 w-5' />,
              description: 'Fast and reliable internet access and solutions',
            },
          ],
        },
        {
          name: 'Qwetu Gas Supplier',
          description: 'Cylinder refills, accessories, and free home delivery',
          href: '/public/services/qwetu-gas-supply',
          icon: <LuFlame className='h-6 w-6 text-[#F97316]' />,
          color: 'from-[#F97316]/20 to-[#C2410C]/20',
          hoverColor:
            'group-hover:from-[#F97316]/30 group-hover:to-[#C2410C]/30',
          accentColor: '#F97316',
          bgColor: 'bg-[#F97316]/10',
          borderColor: 'border-[#F97316]/30',
          subServices: [
            {
              name: 'Cylinder Refills',
              icon: <LuFlame className='h-5 w-5' />,
              description: 'Same-day refills for every major brand',
            },
            {
              name: 'New Cylinder Kits',
              icon: <LuPackage className='h-5 w-5' />,
              description: 'Brand-new cylinders with hose and regulator',
            },
            {
              name: 'Free Home Delivery',
              icon: <LuBike className='h-5 w-5' />,
              description: 'Door-to-door delivery in Kaoyeni at no charge',
            },
          ],
        },
      ],
    },
    { name: 'Contact', href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-gray-900/90 backdrop-blur-md shadow-lg shadow-[#00B0F0]/10'
            : 'bg-transparent'
        }`}
      >
        {/* Decorative Top Border */}
        <div className='absolute top-0 left-0 right-0 h-1 bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050]' />

        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='flex h-16 sm:h-20 items-center justify-between'>
            {/* Logo */}
            <div className='flex items-center'>
              <Link href='/' className='relative group'>
                <div className='absolute -inset-1 rounded-full bg-linear-to-r from-[#00B0F0]/20 via-[#FFA500]/20 to-[#00B050]/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500'></div>
                <div className='relative'>
                  <img
                    src='/images/logo_clear.png'
                    alt='Qwetulands'
                    className='h-10 sm:h-12 w-auto transition-transform duration-300 group-hover:scale-105'
                  />
                </div>
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className='hidden md:block'>
              <div className='ml-10 flex items-center space-x-8'>
                {mainMenuItems.map((item) => (
                  <div key={item.name} className='relative group'>
                    {item.submenu ? (
                      <button
                        onClick={() => setActiveDropdown(!activeDropdown)}
                        onMouseEnter={() => setActiveDropdown(true)}
                        onMouseLeave={() => setActiveDropdown(false)}
                        className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                          isScrolled
                            ? 'text-white'
                            : 'text-white hover:text-[#00B0F0]'
                        }`}
                      >
                        <span>{item.name}</span>
                        <LuChevronDown
                          className={`ml-1 h-4 w-4 transition-transform ${
                            activeDropdown ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    ) : (
                      <Link
                        href={item.href}
                        className={`text-sm font-medium transition-colors ${
                          isScrolled
                            ? 'text-white hover:text-[#00B0F0]'
                            : 'text-white hover:text-[#00B0F0]'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )}

                    {/* Mega Menu dropdown for Services */}
                    {item.submenu && (
                      <div
                        onMouseEnter={() => setActiveDropdown(true)}
                        onMouseLeave={() => setActiveDropdown(false)}
                        className={`absolute left-1/2 top-full z-20 mt-2 w-screen max-w-5xl -translate-x-1/2 transform px-4 sm:px-6 lg:px-8 ${
                          activeDropdown
                            ? 'opacity-100 translate-y-0 visible'
                            : 'opacity-0 -translate-y-5 invisible'
                        } transition-all duration-300`}
                      >
                        <div className='overflow-hidden rounded-2xl border border-gray-700/20 bg-gray-900 backdrop-blur-xl shadow-xl shadow-[#00B0F0]/10'>
                          {/* Decorative Top Border */}
                          <div className='h-1 w-full bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050]' />

                          <div className='grid grid-cols-2 md:grid-cols-3 gap-3 p-4 sm:p-6'>
                            {item.submenu.map((service) => (
                              <div
                                key={service.name}
                                className='group relative h-full'
                              >
                                <Link
                                  href={service.href}
                                  className='flex h-full flex-col rounded-xl border border-gray-800 bg-gray-800/50 p-5 transition-colors hover:border-gray-700 hover:bg-gray-800/80'
                                >
                                  <div
                                    className={`mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-linear-to-br ${service.color}`}
                                  >
                                    {service.icon}
                                  </div>
                                  <div>
                                    <h3 className='font-medium text-white'>
                                      {service.name}
                                    </h3>
                                    <p className='mt-1 text-sm text-gray-400 line-clamp-2'>
                                      {service.description}
                                    </p>

                                    <div className='mt-3 flex flex-wrap gap-1.5'>
                                      {service.subServices
                                        .slice(0, 3)
                                        .map((subService) => (
                                          <span
                                            key={subService.name}
                                            className={`inline-flex items-center rounded-full bg-${service.accentColor}/10 ${service.borderColor} px-2.5 py-0.5 text-xs`}
                                          >
                                            {subService.name}
                                          </span>
                                        ))}
                                    </div>
                                  </div>
                                  <div className='mt-auto pt-3 flex items-center text-sm text-[#00B0F0] group-hover:text-[#00B0F0]/80'>
                                    <span>Learn more</span>
                                    <LuArrowRight className='ml-1 h-4 w-4' />
                                  </div>
                                </Link>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Login and Mobile Menu Buttons */}
            <div className='flex items-center space-x-3'>
              {!isSignedIn ? (
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className='flex items-center space-x-2 rounded-lg bg-linear-to-r from-[#00B0F0] to-[#0090D0] px-4 py-2 text-sm font-medium text-white transition-transform duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#00B0F0]/20'
                >
                  <LuLogIn className='h-4 w-4' />
                  <span>Login</span>
                </button>
              ) : (
                <div className='flex items-center space-x-3'>
                  <Link
                    href='/dashboard'
                    className='hidden md:flex items-center space-x-1 text-sm text-white/80 hover:text-white transition-colors'
                  >
                    <LuLayoutDashboard className='h-4 w-4' />
                    <span>Dashboard</span>
                  </Link>

                  <UserButton />
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className='md:hidden rounded-lg bg-gray-800/30 p-2 text-gray-300 hover:bg-gray-800/70 hover:text-white transition-colors'
              >
                {isMobileMenuOpen ? (
                  <LuX className='h-5 w-5' />
                ) : (
                  <LuMenu className='h-5 w-5' />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen
              ? 'max-h-[80vh] opacity-100 overflow-y-auto'
              : 'max-h-0 opacity-0 overflow-hidden'
          } bg-gray-900/95 backdrop-blur-md shadow-lg shadow-[#00B0F0]/10 border-t border-gray-800`}
        >
          <div className='space-y-2 px-4 py-4 pb-6'>
            {mainMenuItems.map((item) => (
              <div key={item.name}>
                {item.submenu ? (
                  <div className='space-y-3 py-2'>
                    <div className='text-sm font-semibold text-white border-b border-gray-700/50 pb-2 flex items-center'>
                      <span className='bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050] bg-clip-text text-transparent'>
                        {item.name}
                      </span>
                    </div>
                    <div className='grid grid-cols-1 gap-3'>
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          href={subItem.href}
                          className='flex items-start space-x-3 rounded-lg p-3 bg-gray-800/30 transition-all duration-200 hover:bg-gray-800/70 active:scale-[0.98]'
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div
                            className={`shrink-0 rounded-lg bg-linear-to-br ${subItem.color} p-2`}
                          >
                            {subItem.icon}
                          </div>
                          <div className='flex-1'>
                            <h4 className='text-sm font-medium text-white'>
                              {subItem.name}
                            </h4>
                            <p className='mt-1 text-xs text-gray-400 line-clamp-2'>
                              {subItem.description}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    href={item.href}
                    className='block rounded-lg px-4 py-3 text-gray-300 transition-colors hover:bg-gray-800/50 hover:text-[#00B0F0] active:scale-[0.98]'
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}

            {/* Mobile Login Button */}
            <div className='mt-5 pt-4 border-t border-gray-700/50'>
              {!isSignedIn ? (
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className='flex items-center justify-center w-full space-x-2 rounded-lg bg-linear-to-r from-[#00B0F0] to-[#0090D0] px-4 py-3 text-sm font-medium text-white active:scale-[0.98] shadow-lg shadow-[#00B0F0]/20'
                >
                  <LuLogIn className='h-4 w-4' />
                  <span>Login</span>
                </button>
              ) : (
                <div className='space-y-3'>
                  {/* User info for mobile */}
                  <div className='flex items-center p-3 bg-gray-800/40 rounded-lg'>
                    <div className='w-8 h-8 rounded-full bg-[#00B0F0]/20 flex items-center justify-center mr-3'>
                      <LuUser className='h-4 w-4 text-[#00B0F0]' />
                    </div>
                    <div className='text-white opacity-90 text-sm'>
                      {userName}
                    </div>
                  </div>

                  {/* Dashboard link for mobile */}
                  <Link
                    href='/dashboard'
                    className='flex items-center justify-center w-full space-x-2 rounded-lg bg-linear-to-r from-[#00B0F0] to-[#0090D0] px-4 py-3 text-sm font-medium text-white active:scale-[0.98] shadow-lg shadow-[#00B0F0]/20'
                  >
                    <LuLayoutDashboard className='h-4 w-4' />
                    <span>Dashboard</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </>
  );
};

export default NavigationBar;
